import { Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";

import { TensileRequirementByGrade } from "@/pages/configuration/models/TensileRequirementByGrade";
import { HeatMechanicalResult } from "../../models/HeatMechanicalResult";
import {
	formatMechanicalValue,
	formatRange,
	getSampleStatusLabel,
} from "./format";
import { Disposition, MechanicalResultValues } from "./types";

type ValueColumn = {
	field: keyof MechanicalResultValues;
	label: string;
	value: (result: HeatMechanicalResult) => number | null;
	passed: (result: HeatMechanicalResult) => boolean | null;
	format: (result: HeatMechanicalResult) => string | number;
	range: (requirement: TensileRequirementByGrade) => string;
};

export const VALUE_COLUMNS: ValueColumn[] = [
	{
		field: "yieldValue",
		label: "Yield",
		value: (r) => r.YieldValue,
		passed: (r) => r.YieldPassed,
		format: (r) =>
			formatMechanicalValue(
				r.YieldValue,
				r.YieldPassed,
				r.YieldMin,
				r.YieldMax,
				3,
				r.YieldMinKsi,
				r.YieldMaxKsi,
			),
		range: (r) => formatRange(r.YieldMin, r.YieldMax, r.Unit?.trim() || null),
	},
	{
		field: "tensileValue",
		label: "Tensile",
		value: (r) => r.TensileValue,
		passed: (r) => r.TensilePassed,
		format: (r) =>
			formatMechanicalValue(
				r.TensileValue,
				r.TensilePassed,
				r.TensileMin,
				r.TensileMax,
				3,
				r.TensileMinKsi,
				r.TensileMaxKsi,
			),
		range: (r) =>
			formatRange(r.TensileMin, r.TensileMax, r.Unit?.trim() || null),
	},
	{
		field: "elongationValue",
		label: "Elong.",
		value: (r) => r.ElongationValue,
		passed: (r) => r.ElongationPassed,
		format: (r) =>
			formatMechanicalValue(
				r.ElongationValue,
				r.ElongationPassed,
				r.ElongationPercentMin,
				null,
			),
		range: (r) => formatRange(r.ElongationPercentMin, null, "%"),
	},
	{
		field: "percentRAValue",
		label: "% RA",
		value: (r) => r.PercentRAValue,
		passed: (r) => r.PercentRAPassed,
		format: (r) =>
			formatMechanicalValue(
				r.PercentRAValue,
				r.PercentRAPassed,
				r.PercentRAMin,
				r.PercentRAMax,
			),
		range: (r) => formatRange(r.PercentRAMin, r.PercentRAMax, "%"),
	},
];

export const changeColor = (
	kind: "results" | "disposition",
	code?: Disposition | null,
) =>
	kind === "results"
		? ("info" as const)
		: code
			? getSampleStatusLabel(code).color
			: ("default" as const);

export const changedCellSx = (
	kind: "results" | "disposition",
	code?: Disposition | null,
) => {
	const color = changeColor(kind, code);
	const main = (theme: Theme) =>
		color === "default" ? theme.palette.grey[600] : theme.palette[color].main;

	return {
		outline: "1.5px solid",
		outlineOffset: "-2px",
		outlineColor: main,
		bgcolor: (theme: Theme) => alpha(main(theme), 0.08),
	};
};

export const headerCellSx = {
	fontSize: 11,
	color: "text.secondary",
	top: 0,
	height: 26,
	py: 0.25,
	bgcolor: "background.paper",
};

export const refCellSx = {
	position: "sticky" as const,
	bottom: 0,
	zIndex: 2,
	fontSize: 11,
	fontWeight: 600,
	color: "text.secondary",
	height: 26,
	py: 0.25,
	bgcolor: "background.paper",
	borderTop: 2,
	borderBottom: 0,
	borderColor: "divider",
	whiteSpace: "nowrap",
};
